import { Button, Col, COLORS, Row, Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import styled from 'styled-components';
import React from 'react';
import { useDispatch } from 'react-redux';
import { openModal } from 'actions/modal';
import { MODAL_FORM } from 'constants/form';
import { useParams } from 'react-router-dom';
import { useCallback } from 'react';

const Container = styled.div`
  max-width: 900px;
  padding: 2rem;
  background: ${COLORS.WHITE};
  line-height: 1.6;
`;

const List = styled.ul`
  margin-left: 1.5rem;
  padding-left: 0;
`;

const ListItem = styled.li`
  margin: 0.4rem 0;
`;

const Highlight = styled.span`
  font-weight: 600;
  color: ${COLORS.BLACK};
`;

const PausePolicy = () => {
  const { teacherId } = useParams();
  const dispatch = useDispatch();
  const handlePress = url => {
    return window.open(url, '_blank');
  };

  const handleOpenAddPauseUnitModal = useCallback(() => {
    dispatch(
      openModal(
        MODAL_FORM.PAUSE_UNITS_MODAL,
        {
          size: 'custom',
          width: `650px`,
        },
        {
          teacherId,
        },
      ),
    );
  }, []);

  return (
    <Container>
      <Row>
        <Text variant="h4" color={COLORS.BLACK}>
          New Teacher Pause Policy <br />
          (Started from 11 July 2025)
        </Text>
        <Col autoRight>
          <Button
            size="small"
            label="Teacher leave usage"
            onClick={() =>
              handlePress(
                `https://admin.cuemath.com/classroom/admin/teacherleaveusage/?search=${teacherId}`,
              )
            }
          />
        </Col>
        <Col>
          <Button
            size="small"
            label="Teacher leave grant"
            onClick={() =>
              handlePress(
                `https://admin.cuemath.com/classroom/admin/teacherleavegrant/?search=${teacherId}`,
              )
            }
          />
        </Col>
        <Col>
          <Button
            size="small"
            label="Add pause units"
            onClick={handleOpenAddPauseUnitModal}
          />
        </Col>
      </Row>
      <Separator transparent height="16" />
      <Text variant="body1" color={COLORS.BLACK}>
        This document outlines our revised Pause Policy for Cuemath teachers. The new
        policy addresses several challenges in the current system by introducing a
        simplified, user-friendly approach based on better pause application logic.
      </Text>
      <Text variant="body1" color={COLORS.BLACK}>
        Like our current policy, the new policy also has two kinds of pauses:
      </Text>
      <List>
        <ListItem>
          <Text variant="body1" color={COLORS.BLACK}>
            1. Pause Future Classes (Earlier, Planned Pause)
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="body1" color={COLORS.BLACK}>
            2. Pause Upcoming Classes (Earlier, Emergency Pause)
          </Text>
        </ListItem>
      </List>
      <Text variant="body1" color={COLORS.BLACK}>
        Let’s go through them one by one.
      </Text>
      <Separator transparent height="16" />
      <Text variant="h6" color={COLORS.BLACK}>
        1. Pause Future Classes
      </Text>
      <List>
        <ListItem>
          - Teachers can use <Highlight>24 planned pause days</Highlight> per year.
        </ListItem>
        <ListItem>
          - Applicable only for classes scheduled at least{' '}
          <Highlight>24 hours in advance</Highlight>.
        </ListItem>
        <ListItem>- Must be applied to entire days (no partial days).</ListItem>
        <ListItem>
          - Pause days are assigned on <Highlight>Jan 1</Highlight> and expire on{' '}
          <Highlight>Dec 31</Highlight> (no carry forward).
        </ListItem>
        <ListItem>
          - Teachers joining mid-year get pause days on a{' '}
          <Highlight>pro rata basis</Highlight>. Example: joining in June → 6 months
          allocation, then standard 2 days/month from next year.
        </ListItem>
      </List>
      <Text variant="h6" color={COLORS.BLACK}>
        2. Pause Upcoming Classes
      </Text>
      <List>
        <ListItem>
          - Emergency pauses allowed up to <Highlight>15 minutes before class</Highlight>.
        </ListItem>
        <ListItem>- Only for classes within the next 24 hours.</ListItem>
        <ListItem>- Half-day pauses are allowed:</ListItem>
        <List>
          <ListItem>
            1. If ≤ 50% of day’s classes are paused →{' '}
            <Highlight>0.5 pause days</Highlight>.
          </ListItem>
          <ListItem>
            2. If &gt; 50% of day’s classes are paused →{' '}
            <Highlight>1 pause day</Highlight>.
          </ListItem>
        </List>
        <ListItem>
          - Allocated on the 1st of every month, with{' '}
          <Highlight>no carry forward</Highlight>.
        </ListItem>
        <Highlight>Pause Allocation</Highlight>
        <ListItem>
          - On Jan 1: <Highlight>26 pause days</Highlight> (24 + 2).
        </ListItem>
        <ListItem>
          - 2 pause days are added on the <Highlight>1st of each month</Highlight> until
          Dec 1.
        </ListItem>
        <ListItem>
          - On Dec 31: all unused pause days <Highlight>expire</Highlight>.
        </ListItem>
      </List>
    </Container>
  );
};

export default PausePolicy;
